// client/src/user/support/UserSupportTickets.js
import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Box,
  Typography,
  Stack,
  Button,
  TextField,
  Chip,
  Avatar,
  Divider,
  Paper,
  IconButton,
  useTheme,
  alpha,
  CircularProgress,
} from "@mui/material";
import {
  Add as AddIcon,
  Send as SendIcon,
  ArrowBack as BackIcon,
  SupportAgent as SupportIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  ConfirmationNumber as TicketIcon,
} from "@mui/icons-material";
import { GlobalContext } from "../../context/GlobalContext";
import { TranslateContext } from "../../context/TranslateContext";
import CommonDialog from "../../common/CommonDialog";

const L = {
  support: "Support",
  myTickets: "My Tickets",
  newTicket: "New Ticket",
  subject: "Subject",
  message: "Message",
  submit: "Submit",
  cancel: "Cancel",
  noTickets: "No tickets yet",
  noTicketsDesc: "Submit a ticket and we'll get back to you.",
  open: "Open",
  answered: "Answered",
  closed: "Closed",
  typeReply: "Type a reply...",
  send: "Send",
  originalMessage: "Original Message",
  replies: "Replies",
  back: "Back",
  ticketClosed: "This ticket is closed.",
  subjectPlaceholder: "Brief description of your issue",
  messagePlaceholder: "Describe your issue in detail...",
};

const STATUS_COLORS = {
  open: "error",
  answered: "success",
  closed: "default",
};

const UserSupportTickets = ({ lang: propLang }) => {
  const theme = useTheme();
  const { hitAxios } = useContext(GlobalContext);
  const { lang: ctxLang } = useContext(TranslateContext);
  const t = (k) => propLang?.[k] || ctxLang?.[k] || L[k] || k;

  const [view, setView] = useState("list"); // "list" | "detail"
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replies, setReplies] = useState([]);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [newTicketOpen, setNewTicketOpen] = useState(false);
  const [form, setForm] = useState({ subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchTickets();
  }, []);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [replies]);

  async function fetchTickets() {
    setLoading(true);
    const res = await hitAxios({
      path: "/api/support/user/list",
      post: false,
      showLoading: false,
      showSnackbar: false,
    });
    setLoading(false);
    if (res?.data?.success) setTickets(res.data.data || []);
  }

  async function openTicket(ticket) {
    setSelectedTicket(ticket);
    setView("detail");
    const res = await hitAxios({
      path: `/api/support/user/replies/${ticket.ticket_id}`,
      post: false,
      showLoading: false,
      showSnackbar: false,
    });
    if (res?.data?.success) {
      setReplies(res.data.data || []);
      setSelectedTicket(res.data.ticket);
    }
  }

  async function handleSendReply() {
    if (!replyText.trim() || !selectedTicket) return;
    setSending(true);
    const res = await hitAxios({
      path: "/api/support/user/reply",
      post: true,
      obj: { ticket_id: selectedTicket.ticket_id, message: replyText.trim() },
    });
    setSending(false);
    if (res?.data?.success) {
      setReplyText("");
      const r2 = await hitAxios({
        path: `/api/support/user/replies/${selectedTicket.ticket_id}`,
        post: false,
        showLoading: false,
        showSnackbar: false,
      });
      if (r2?.data?.success) {
        setReplies(r2.data.data || []);
        setSelectedTicket(r2.data.ticket);
      }
      fetchTickets();
    }
  }

  async function handleSubmitTicket() {
    if (!form.subject.trim() || !form.message.trim()) return;
    setSubmitting(true);
    const res = await hitAxios({
      path: "/api/support/user/create",
      post: true,
      obj: form,
    });
    setSubmitting(false);
    if (res?.data?.success) {
      setNewTicketOpen(false);
      setForm({ subject: "", message: "" });
      fetchTickets();
    }
  }

  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : "—";

  // ── List view ──
  if (view === "list") {
    return (
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: `1px solid ${theme.palette.divider}`,
            flexShrink: 0,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <SupportIcon sx={{ fontSize: 18, color: "primary.main" }} />
            <Typography variant="body2" fontWeight={700} color="text.primary">
              {t("support")}
            </Typography>
          </Stack>
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon sx={{ fontSize: 14 }} />}
            onClick={() => setNewTicketOpen(true)}
            sx={{
              textTransform: "none",
              fontSize: "0.75rem",
              borderRadius: 2,
              py: 0.6,
            }}
          >
            {t("newTicket")}
          </Button>
        </Box>

        {/* Ticket list */}
        <Box sx={{ flex: 1, overflowY: "auto", p: 1.5 }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", pt: 4 }}>
              <CircularProgress size={22} sx={{ color: "primary.main" }} />
            </Box>
          ) : tickets.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 6, px: 2 }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "14px",
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 1.5,
                }}
              >
                <TicketIcon sx={{ fontSize: 22, color: "text.disabled" }} />
              </Box>
              <Typography
                variant="caption"
                color="text.disabled"
                display="block"
                fontWeight={500}
                sx={{ fontSize: "0.75rem" }}
              >
                {t("noTickets")}
              </Typography>
              <Typography
                variant="caption"
                color="text.disabled"
                display="block"
                sx={{ fontSize: "0.68rem", mt: 0.5 }}
              >
                {t("noTicketsDesc")}
              </Typography>
            </Box>
          ) : (
            <Stack spacing={0.75}>
              {tickets.map((ticket) => (
                <Box
                  key={ticket.ticket_id}
                  onClick={() => openTicket(ticket)}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`,
                    bgcolor: "background.paper",
                    cursor: "pointer",
                    transition: "all 0.12s",
                    "&:hover": {
                      borderColor: alpha(theme.palette.primary.main, 0.4),
                      bgcolor: alpha(theme.palette.primary.main, 0.03),
                    },
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="flex-start"
                    justifyContent="space-between"
                    spacing={1}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      noWrap
                      sx={{ flex: 1, fontSize: "0.8125rem" }}
                    >
                      {ticket.subject}
                    </Typography>
                    <Chip
                      label={t(ticket.status) || ticket.status}
                      size="small"
                      color={STATUS_COLORS[ticket.status] || "default"}
                      sx={{ fontSize: "0.6rem", height: 18, flexShrink: 0 }}
                    />
                  </Stack>
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    sx={{ fontSize: "0.65rem", mt: 0.5, display: "block" }}
                  >
                    {formatDate(ticket.createdAt)}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}
        </Box>

        {/* New Ticket Dialog */}
        <CommonDialog
          open={newTicketOpen}
          onClose={() => {
            setNewTicketOpen(false);
            setForm({ subject: "", message: "" });
          }}
          title={t("newTicket")}
          icon={SupportIcon}
          maxWidth="xs"
        >
          <Stack spacing={2.5} mt={1}>
            <TextField
              fullWidth
              autoFocus
              label={t("subject")}
              placeholder={t("subjectPlaceholder")}
              value={form.subject}
              onChange={(e) =>
                setForm((p) => ({ ...p, subject: e.target.value }))
              }
              inputProps={{ maxLength: 200 }}
            />
            <TextField
              fullWidth
              multiline
              rows={4}
              label={t("message")}
              placeholder={t("messagePlaceholder")}
              value={form.message}
              onChange={(e) =>
                setForm((p) => ({ ...p, message: e.target.value }))
              }
            />
            <Stack direction="row" spacing={1.5} justifyContent="flex-end">
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => {
                  setNewTicketOpen(false);
                  setForm({ subject: "", message: "" });
                }}
                sx={{ borderRadius: 2, textTransform: "none" }}
              >
                {t("cancel")}
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmitTicket}
                disabled={
                  !form.subject.trim() || !form.message.trim() || submitting
                }
                sx={{ borderRadius: 2, textTransform: "none" }}
              >
                {t("submit")}
              </Button>
            </Stack>
          </Stack>
        </CommonDialog>
      </Box>
    );
  }

  // ── Detail view ──
  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Box
        sx={{
          px: 1.5,
          py: 1.25,
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
          flexShrink: 0,
        }}
      >
        <IconButton
          size="small"
          onClick={() => {
            setView("list");
            setReplies([]);
            setSelectedTicket(null);
          }}
          sx={{ borderRadius: 1.5 }}
        >
          <BackIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            fontWeight={700}
            noWrap
            sx={{ fontSize: "0.8125rem" }}
          >
            {selectedTicket?.subject}
          </Typography>
        </Box>
        {selectedTicket?.status && (
          <Chip
            label={t(selectedTicket.status) || selectedTicket.status}
            size="small"
            color={STATUS_COLORS[selectedTicket.status] || "default"}
            sx={{ fontSize: "0.6rem", height: 18, flexShrink: 0 }}
          />
        )}
      </Box>

      {/* Messages */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          p: 1.5,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          "&::-webkit-scrollbar": { width: 3 },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: alpha(theme.palette.text.primary, 0.12),
            borderRadius: 2,
          },
        }}
      >
        {/* Original message */}
        {selectedTicket && (
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
            }}
          >
            <Avatar
              sx={{
                width: 28,
                height: 28,
                flexShrink: 0,
                bgcolor: alpha(theme.palette.primary.main, 0.15),
              }}
            >
              <PersonIcon sx={{ fontSize: 14, color: "primary.main" }} />
            </Avatar>
            <Box
              sx={{
                maxWidth: "85%",
                p: 1.25,
                borderRadius: "4px 12px 12px 12px",
                bgcolor: "action.hover",
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: "pre-wrap",
                  lineHeight: 1.65,
                  fontSize: "0.8375rem",
                }}
              >
                {selectedTicket.message}
              </Typography>
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ fontSize: "0.65rem", mt: 0.5, display: "block" }}
              >
                {new Date(selectedTicket.createdAt).toLocaleString()}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Replies */}
        {replies.map((reply) => {
          const isAdmin = reply.sender_role === "admin";
          return (
            <Box
              key={reply.id}
              sx={{
                display: "flex",
                flexDirection: isAdmin ? "row-reverse" : "row",
                alignItems: "flex-start",
                gap: 1,
              }}
            >
              <Avatar
                sx={{
                  width: 28,
                  height: 28,
                  flexShrink: 0,
                  bgcolor: isAdmin
                    ? "primary.main"
                    : alpha(theme.palette.primary.main, 0.15),
                }}
              >
                {isAdmin ? (
                  <AdminIcon
                    sx={{ fontSize: 14, color: "primary.contrastText" }}
                  />
                ) : (
                  <PersonIcon sx={{ fontSize: 14, color: "primary.main" }} />
                )}
              </Avatar>
              <Box
                sx={{
                  maxWidth: "85%",
                  p: 1.25,
                  borderRadius: isAdmin
                    ? "12px 4px 12px 12px"
                    : "4px 12px 12px 12px",
                  bgcolor: isAdmin
                    ? alpha(theme.palette.primary.main, 0.1)
                    : "action.hover",
                  border: `1px solid ${
                    isAdmin
                      ? alpha(theme.palette.primary.main, 0.2)
                      : theme.palette.divider
                  }`,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: "pre-wrap",
                    lineHeight: 1.65,
                    fontSize: "0.8375rem",
                  }}
                >
                  {reply.message}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.disabled"
                  sx={{ fontSize: "0.65rem", mt: 0.5, display: "block" }}
                >
                  {new Date(reply.createdAt).toLocaleString()}
                </Typography>
              </Box>
            </Box>
          );
        })}
        <div ref={messagesEndRef} />
      </Box>

      {/* Reply input */}
      <Box
        sx={{
          px: 1.5,
          py: 1.25,
          borderTop: `1px solid ${theme.palette.divider}`,
          flexShrink: 0,
        }}
      >
        {selectedTicket?.status === "closed" ? (
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ display: "block", textAlign: "center", py: 0.5 }}
          >
            {t("ticketClosed")}
          </Typography>
        ) : (
          <Stack direction="row" spacing={1} alignItems="flex-end">
            <TextField
              fullWidth
              multiline
              maxRows={3}
              size="small"
              placeholder={t("typeReply")}
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSendReply();
                }
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  fontSize: "0.8375rem",
                },
              }}
            />
            <IconButton
              onClick={handleSendReply}
              disabled={!replyText.trim() || sending}
              sx={{
                bgcolor: "primary.main",
                color: "primary.contrastText",
                borderRadius: 2,
                width: 36,
                height: 36,
                flexShrink: 0,
                "&:hover": { bgcolor: "primary.dark" },
                "&.Mui-disabled": { bgcolor: "action.disabledBackground" },
              }}
            >
              <SendIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default UserSupportTickets;
